// Phase 92: full-screen Camera-Raw-style edit panel.
//
// Real-time WYSIWYG global adjustments applied via CSS filters + a
// SVG feColorMatrix layer over the JPEG preview. Adjustments are
// persisted as an Adobe-compatible XMP sidecar next to the CR2 via
// ``POST /api/photos/{id}/raw-edit``. "Apply to group" replicates
// the XMP to every other CR2 in the same AI group.
//
// Server-side full-resolution JPEG export (via darktable-cli) is
// Phase 93; this panel only handles the interactive edit + XMP save.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Save, RotateCcw, Users, Download, Circle, Slash, Trash2, ZoomIn, ZoomOut, Maximize, Move } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_ADJUSTMENTS = {
  Exposure2012: 0.0,
  Contrast2012: 0.0,
  Highlights2012: 0.0,
  Shadows2012: 0.0,
  Whites2012: 0.0,
  Blacks2012: 0.0,
  Vibrance: 0.0,
  Saturation: 0.0,
  Temperature: 0,  // Adobe stores as absolute Kelvin; we use offset -100..100 here for simplicity.
  Tint: 0,
  Clarity2012: 0.0,
  Dehaze: 0.0,
  // Phase 95: Detail panel — Adobe Camera Raw defaults for a clean
  // baseline. Users typically don't want Sharpness=0 (that DISABLES
  // sharpening entirely, producing a soft render), so we ship with
  // ACR's out-of-box values and let the user dial from there.
  Sharpness: 25,
  SharpenRadius: 1.0,
  SharpenDetail: 25,
  SharpenEdgeMasking: 0,
  LuminanceSmoothing: 0,
  LuminanceNoiseReductionDetail: 50,
  ColorNoiseReduction: 25,
  ColorNoiseReductionDetail: 50,
};

const SLIDER_CONFIG = [
  { section: "Light", key: "Exposure2012",   label: "Exposure",   min: -5,   max: 5,   step: 0.05 },
  { section: "Light", key: "Contrast2012",   label: "Contrast",   min: -100, max: 100, step: 1 },
  { section: "Light", key: "Highlights2012", label: "Highlights", min: -100, max: 100, step: 1 },
  { section: "Light", key: "Shadows2012",    label: "Shadows",    min: -100, max: 100, step: 1 },
  { section: "Light", key: "Whites2012",     label: "Whites",     min: -100, max: 100, step: 1 },
  { section: "Light", key: "Blacks2012",     label: "Blacks",     min: -100, max: 100, step: 1 },
  { section: "Colour", key: "Temperature",   label: "Temperature (offset)", min: -100, max: 100, step: 1 },
  { section: "Colour", key: "Tint",          label: "Tint (offset)",        min: -100, max: 100, step: 1 },
  { section: "Colour", key: "Vibrance",      label: "Vibrance",   min: -100, max: 100, step: 1 },
  { section: "Colour", key: "Saturation",    label: "Saturation", min: -100, max: 100, step: 1 },
  { section: "Presence", key: "Clarity2012", label: "Clarity",    min: -100, max: 100, step: 1 },
  { section: "Presence", key: "Dehaze",      label: "Dehaze",     min: -100, max: 100, step: 1 },
];

// Phase 95: Detail panel — sharpening + noise reduction sliders.
// Adobe's ranges are exposed 1:1 so the same numbers Lightroom shows
// under Detail are stored verbatim in the XMP.
const DETAIL_SLIDER_CONFIG = [
  { section: "Sharpening", key: "Sharpness",          label: "Amount", min: 0,   max: 150, step: 1 },
  { section: "Sharpening", key: "SharpenRadius",      label: "Radius", min: 0.5, max: 3,   step: 0.1 },
  { section: "Sharpening", key: "SharpenDetail",      label: "Detail", min: 0,   max: 100, step: 1 },
  { section: "Sharpening", key: "SharpenEdgeMasking", label: "Masking", min: 0,  max: 100, step: 1 },
  { section: "Noise Reduction · Luminance", key: "LuminanceSmoothing",              label: "Amount", min: 0, max: 100, step: 1 },
  { section: "Noise Reduction · Luminance", key: "LuminanceNoiseReductionDetail",   label: "Detail", min: 0, max: 100, step: 1 },
  { section: "Noise Reduction · Colour",    key: "ColorNoiseReduction",             label: "Amount", min: 0, max: 100, step: 1 },
  { section: "Noise Reduction · Colour",    key: "ColorNoiseReductionDetail",       label: "Detail", min: 0, max: 100, step: 1 },
];

const HSL_CHANNELS = [
  { key: "Red",     tint: "#ef4444" },
  { key: "Orange",  tint: "#f97316" },
  { key: "Yellow",  tint: "#eab308" },
  { key: "Green",   tint: "#22c55e" },
  { key: "Aqua",    tint: "#06b6d4" },
  { key: "Blue",    tint: "#3b82f6" },
  { key: "Purple",  tint: "#a855f7" },
  { key: "Magenta", tint: "#ec4899" },
];

const DEFAULT_HSL = Object.fromEntries(
  HSL_CHANNELS.map((c) => [c.key, { h: 0, s: 0, l: 0 }]),
);

const DEFAULT_CURVE = [[0, 0], [255, 255]];  // identity

// Phase 94: Local-adjustment masks (Radial + Linear gradient).
// One mask per photo for now; type=null means "no mask defined".
// Geometry coords are normalized 0..1 relative to the rendered
// preview image so they survive different display sizes.
const DEFAULT_MASK = null;
const NEW_RADIAL_MASK = () => ({
  type: "radial",
  cx: 0.5, cy: 0.5,
  rx: 0.25, ry: 0.25,
  feather: 0.5,
  invert: false,
  adjustments: {
    Exposure2012: 0.0, Contrast2012: 0.0,
    Highlights2012: 0.0, Shadows2012: 0.0,
    Whites2012: 0.0, Blacks2012: 0.0,
    Temperature: 0, Tint: 0,
    Saturation: 0.0, Clarity2012: 0.0,
  },
});
const NEW_LINEAR_MASK = () => ({
  type: "linear",
  // Two endpoints: full effect at (x0,y0), no effect at (x1,y1).
  x0: 0.5, y0: 0.15,
  x1: 0.5, y1: 0.85,
  invert: false,
  adjustments: {
    Exposure2012: 0.0, Contrast2012: 0.0,
    Highlights2012: 0.0, Shadows2012: 0.0,
    Whites2012: 0.0, Blacks2012: 0.0,
    Temperature: 0, Tint: 0,
    Saturation: 0.0, Clarity2012: 0.0,
  },
});

const MASK_SLIDER_CONFIG = [
  { key: "Exposure2012",   label: "Exposure",   min: -5,   max: 5,   step: 0.05 },
  { key: "Contrast2012",   label: "Contrast",   min: -100, max: 100, step: 1 },
  { key: "Highlights2012", label: "Highlights", min: -100, max: 100, step: 1 },
  { key: "Shadows2012",    label: "Shadows",    min: -100, max: 100, step: 1 },
  { key: "Whites2012",     label: "Whites",     min: -100, max: 100, step: 1 },
  { key: "Blacks2012",     label: "Blacks",     min: -100, max: 100, step: 1 },
  { key: "Temperature",    label: "Temp (offset)", min: -100, max: 100, step: 1 },
  { key: "Tint",           label: "Tint (offset)", min: -100, max: 100, step: 1 },
  { key: "Saturation",     label: "Saturation", min: -100, max: 100, step: 1 },
  { key: "Clarity2012",    label: "Clarity",    min: -100, max: 100, step: 1 },
];

// Translate ACR-style slider values into CSS filters + brightness
// tweaks. This is a real-time APPROXIMATION for the WYSIWYG
// preview — the final full-res JPEG export in Phase 93 will run
// through darktable which does the proper linear pipeline.
function adjustmentsToCssFilter(adj) {
  // Exposure2012 is in EV stops. 1 EV = 2x brightness → we clamp
  // to a reasonable multiplier and expose it as brightness().
  const brightness = Math.pow(2, (adj.Exposure2012 || 0) * 0.9); // slightly damped
  const contrast = 1 + (adj.Contrast2012 || 0) / 200; // -100..100 → 0.5..1.5
  const saturation = 1 + (adj.Saturation || 0) / 100;
  // Approximation: Vibrance boosts less saturated pixels — CSS has
  // no equivalent, so blend a portion of it into saturation.
  const vibrance = 1 + (adj.Vibrance || 0) / 200;

  // Phase 97: HSL preview — the real per-channel hue-shift needs a
  // canvas or shader. As a light-touch approximation we aggregate
  // the 8 channels' H/S/L means into a single global hue-rotate +
  // saturation + brightness nudge. Move a single channel and you'll
  // see the whole image tint — the FINAL darktable export applies
  // the per-channel math correctly.
  let hueDeg = 0, hslSatBoost = 0, hslLightBoost = 0;
  if (adj.HSL && typeof adj.HSL === "object") {
    const chans = Object.values(adj.HSL);
    let hSum = 0, sSum = 0, lSum = 0, n = 0;
    for (const c of chans) {
      if (!c) continue;
      hSum += Number(c.h) || 0;
      sSum += Number(c.s) || 0;
      lSum += Number(c.l) || 0;
      n++;
    }
    if (n > 0) {
      hueDeg = (hSum / n) * 0.3;         // -30..30 deg
      hslSatBoost = (sSum / n) / 200;    // ±0.5
      hslLightBoost = (lSum / n) / 400;  // ±0.25
    }
  }

  // Phase 97: Detail preview — Sharpness > baseline (25) nudges
  // contrast up; below baseline softens via blur. LuminanceSmoothing
  // maps to CSS blur (0..1.2px) so noise reduction reads visibly.
  const sharp = (adj.Sharpness ?? 25);
  const sharpContrast = 1 + Math.max(-0.15, Math.min(0.15, (sharp - 25) / 200));
  const lumSmooth = Math.max(0, Math.min(100, adj.LuminanceSmoothing || 0));
  const blurPx = (lumSmooth / 100) * 1.2 + Math.max(0, (25 - sharp) / 200);

  // Phase 98.2: Clarity nudges mid-contrast; Dehaze combines a
  // stronger contrast boost with a saturation lift so foggy shots
  // pop the way ACR's Dehaze does. Both were previously ignored by
  // the preview filter.
  const clarityContrast = 1 + (adj.Clarity2012 || 0) / 200;
  const dehazeContrast = 1 + (adj.Dehaze || 0) / 150;
  const dehazeSat = 1 + (adj.Dehaze || 0) / 220;

  const parts = [
    `brightness(${(brightness * (1 + hslLightBoost)).toFixed(3)})`,
    `contrast(${(contrast * sharpContrast * clarityContrast * dehazeContrast).toFixed(3)})`,
    `saturate(${(saturation * vibrance * (1 + hslSatBoost) * dehazeSat).toFixed(3)})`,
  ];
  if (Math.abs(hueDeg) > 0.5) parts.push(`hue-rotate(${hueDeg.toFixed(1)}deg)`);
  if (blurPx > 0.05) parts.push(`blur(${blurPx.toFixed(2)}px)`);
  return parts.join(" ");
}

// Temperature (blue↔yellow) and Tint (green↔magenta) via an SVG
// feColorMatrix. Returns a data URL that can be used as a CSS
// ``filter: url(...)``. Rebuilt every keystroke.
function buildTempTintSvg(adj, id) {
  // Simple channel-mixing: warm → red↑ blue↓, tint → green↑ magenta↓.
  const t = (adj.Temperature || 0) / 100;   // -1..1
  const g = (adj.Tint || 0) / 100;          // -1..1
  const rShift = 0.15 * t;   // subtle
  const bShift = -0.15 * t;
  const gShift = 0.10 * g;
  // Highlights + Shadows here are approximated via a lift/gain matrix
  // on the tonal ends. For the real-time preview it's close enough.
  //
  // Phase 98.2: extended to include Whites and Blacks so the four
  // tonal sliders each move the preview independently. Whites lifts
  // the highlight ceiling, Blacks moves the shadow floor.
  const hi = (adj.Highlights2012 || 0) / 200; // -0.5..0.5
  const sh = (adj.Shadows2012 || 0) / 200;
  const wh = (adj.Whites2012 || 0) / 200;
  const bl = (adj.Blacks2012 || 0) / 200;
  const bias = sh * 0.4 + bl * 0.6 + hi * 0.4 + wh * 0.6;
  const matrix = [
    (1 + rShift), 0, 0, 0, bias,
    0, (1 + gShift), 0, 0, bias,
    0, 0, (1 + bShift), 0, bias,
    0, 0, 0, 1, 0,
  ].join(" ");
  const svg = `<svg xmlns='http://www.w3.org/2000/svg'><filter id='${id}'><feColorMatrix in='SourceGraphic' type='matrix' values='${matrix}'/></filter></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}#${id}`;
}

// Phase 97: apply the tone curve to the live preview via an SVG
// ``feComponentTransfer`` filter. We sample the curve at 33 points
// and pass as a ``tableValues`` LUT — cheap and matches darktable's
// bilinear curve interpolation closely enough for WYSIWYG dial-in.
function buildToneCurveSvg(curve, id) {
  // Identity → no filter needed (skip).
  if (!curve || curve.length < 2) return null;
  if (curve.length === 2 &&
      curve[0][0] === 0 && curve[0][1] === 0 &&
      curve[1][0] === 255 && curve[1][1] === 255) {
    return null;
  }
  const sorted = [...curve].sort((a, b) => a[0] - b[0]);
  const N = 33;
  const vals = [];
  for (let i = 0; i < N; i++) {
    const x = (i / (N - 1)) * 255;
    let a = sorted[0], b = sorted[sorted.length - 1];
    for (let j = 0; j < sorted.length - 1; j++) {
      if (x >= sorted[j][0] && x <= sorted[j + 1][0]) {
        a = sorted[j]; b = sorted[j + 1];
        break;
      }
    }
    const dx = b[0] - a[0];
    const t = dx === 0 ? 0 : (x - a[0]) / dx;
    const y = a[1] + t * (b[1] - a[1]);
    vals.push(Math.max(0, Math.min(1, y / 255)).toFixed(4));
  }
  const tv = vals.join(" ");
  const svg = `<svg xmlns='http://www.w3.org/2000/svg'><filter id='${id}'><feComponentTransfer><feFuncR type='table' tableValues='${tv}'/><feFuncG type='table' tableValues='${tv}'/><feFuncB type='table' tableValues='${tv}'/></feComponentTransfer></filter></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}#${id}`;
}

// Phase 98: Photoshop-style Alt-key modifiers.
//
// Track the Alt key at the window level so any tool inside the panel
// (sliders, curve editor, buttons) can react to it in a consistent
// way. Blur / visibility events reset the flag so a user tabbing
// away and back doesn't get stuck in "Alt held" state.
function useAltKey() {
  const [alt, setAlt] = useState(false);
  useEffect(() => {
    const kd = (e) => { if (e.key === "Alt" || e.altKey) setAlt(true); };
    const ku = (e) => { if (e.key === "Alt" || !e.altKey) setAlt(false); };
    const reset = () => setAlt(false);
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    window.addEventListener("blur", reset);
    document.addEventListener("visibilitychange", reset);
    return () => {
      window.removeEventListener("keydown", kd);
      window.removeEventListener("keyup", ku);
      window.removeEventListener("blur", reset);
      document.removeEventListener("visibilitychange", reset);
    };
  }, []);
  return alt;
}

// Map of tonal slider keys → clipping mode. Alt-dragging any of
// these swaps the preview for a threshold view showing where each
// R/G/B channel is (about to be) clipped. Non-tonal keys (HSL, Temp,
// Sharpen params) are intentionally omitted — clipping preview
// doesn't map cleanly onto those.
//
// Phase 98.1: extended to cover every slider with a Photoshop-style
// modifier. Modes:
//   highlight / shadow — tonal clip preview (Basic tonal sliders)
//   grayscale          — luminance-only preview (Colour + Detail Amount)
//   edge               — Laplacian edge mask (Sharpen Radius/Detail/
//                        Masking + Clarity)
//   chroma             — chroma-only preview (Colour NR)
const SLIDER_CLIP_MODE = {
  // Tonal → clip preview
  Exposure2012: "highlight",
  Highlights2012: "highlight",
  Whites2012: "highlight",
  Contrast2012: "highlight",
  Shadows2012: "shadow",
  Blacks2012: "shadow",
  // Colour cast → grayscale so you see luminance without the tint
  Temperature: "grayscale",
  Tint: "grayscale",
  Vibrance: "grayscale",
  Saturation: "grayscale",
  Dehaze: "grayscale",
  // Presence / structure → edge mask
  Clarity2012: "edge",
  // Detail: Sharpening
  Sharpness: "grayscale",         // ACR: Alt = grayscale to see sharpen
  SharpenRadius: "edge",
  SharpenDetail: "edge",
  SharpenEdgeMasking: "edge",     // ACR: iconic "mask preview"
  // Detail: Noise Reduction
  LuminanceSmoothing: "grayscale",
  LuminanceNoiseReductionDetail: "grayscale",
  ColorNoiseReduction: "chroma",
  ColorNoiseReductionDetail: "chroma",
};

const CLIP_MODE_LABEL = {
  highlight: "Highlight clip",
  shadow: "Shadow clip",
  grayscale: "Luminance preview",
  edge: "Edge mask",
  chroma: "Chroma preview",
};

// Build an SVG filter that produces the classic "Alt-drag" clip
// preview:
//   • highlight mode → almost everything crushes to black, only the
//     top ~5 % of each channel lights up in its own colour. When R,
//     G and B all clip you get pure white; a single channel clipping
//     shows its primary; two channels give secondaries.
//   • shadow mode is the inverse — clipped-to-black pixels get lit
//     up in complementary colours over a white background.
function buildClipPreviewSvg(mode, id) {
  if (!mode) return null;
  let body = "";
  if (mode === "highlight") {
    const slope = 20, intercept = -19;
    body = `<feComponentTransfer><feFuncR type='linear' slope='${slope}' intercept='${intercept}'/><feFuncG type='linear' slope='${slope}' intercept='${intercept}'/><feFuncB type='linear' slope='${slope}' intercept='${intercept}'/></feComponentTransfer>`;
  } else if (mode === "shadow") {
    const slope = -20, intercept = 1;
    body = `<feComponentTransfer><feFuncR type='linear' slope='${slope}' intercept='${intercept}'/><feFuncG type='linear' slope='${slope}' intercept='${intercept}'/><feFuncB type='linear' slope='${slope}' intercept='${intercept}'/></feComponentTransfer>`;
  } else if (mode === "grayscale") {
    // Luminance-weighted grayscale (Rec.601 coefficients).
    body = `<feColorMatrix type='matrix' values='0.299 0.587 0.114 0 0  0.299 0.587 0.114 0 0  0.299 0.587 0.114 0 0  0 0 0 1 0'/>`;
  } else if (mode === "edge") {
    // Grayscale → Laplacian edge detector → contrast boost. This is
    // what ACR's iconic "sharpen mask" preview shows: bright edges
    // on a black background so you can tune Radius / Detail /
    // Masking precisely.
    body = `<feColorMatrix type='matrix' values='0.299 0.587 0.114 0 0  0.299 0.587 0.114 0 0  0.299 0.587 0.114 0 0  0 0 0 1 0'/><feConvolveMatrix order='3' kernelMatrix='-1 -1 -1 -1 8 -1 -1 -1 -1' preserveAlpha='true'/><feComponentTransfer><feFuncR type='linear' slope='3' intercept='0'/><feFuncG type='linear' slope='3' intercept='0'/><feFuncB type='linear' slope='3' intercept='0'/></feComponentTransfer>`;
  } else if (mode === "chroma") {
    // Chroma-only view: subtract luminance so only colour remains,
    // then remap around neutral gray so subtle colour noise is
    // visible. Great companion for the Colour NR sliders.
    body = `<feColorMatrix type='matrix' values='0.701 -0.587 -0.114 0 0  -0.299 0.413 -0.114 0 0  -0.299 -0.587 0.886 0 0  0 0 0 1 0'/><feComponentTransfer><feFuncR type='linear' slope='2.5' intercept='0.5'/><feFuncG type='linear' slope='2.5' intercept='0.5'/><feFuncB type='linear' slope='2.5' intercept='0.5'/></feComponentTransfer>`;
  } else {
    return null;
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg'><filter id='${id}'>${body}</filter></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}#${id}`;
}

// Phase 96: real-time WYSIWYG for the local-adjustment mask. We
// stack a SECOND ``<img>`` on top of the base image, CSS-mask it to
// only reveal the shape of the current mask, and apply the merged
// (globals + mask deltas) filter to it. Net effect: the area inside
// the mask shows the local adjustment, everything else shows the
// global-only render. It's an approximation — the FINAL darktable
// export runs the true masked pipeline — but it's WYSIWYG enough to
// dial in numbers by eye.
function buildMaskDataUrl(mask) {
  if (!mask) return null;
  let content = "";
  if (mask.type === "radial") {
    const cx = mask.cx, cy = mask.cy;
    const r = Math.max(0.001, Math.max(mask.rx, mask.ry));
    const midStop = Math.max(0.01, Math.min(0.99, 1 - (mask.feather ?? 0.5)));
    const s0 = mask.invert ? 0 : 1;
    const s1 = mask.invert ? 0 : 0.75;
    const s2 = mask.invert ? 1 : 0;
    content = `
      <defs>
        <radialGradient id="m" cx="${cx}" cy="${cy}" r="${r}" fx="${cx}" fy="${cy}">
          <stop offset="0" stop-color="white" stop-opacity="${s0}" />
          <stop offset="${midStop}" stop-color="white" stop-opacity="${s1}" />
          <stop offset="1" stop-color="white" stop-opacity="${s2}" />
        </radialGradient>
      </defs>
      <rect width="1" height="1" fill="url(#m)" />
    `;
  } else if (mask.type === "linear") {
    const s0 = mask.invert ? 0 : 1;
    const s1 = mask.invert ? 1 : 0;
    content = `
      <defs>
        <linearGradient id="m" x1="${mask.x0}" y1="${mask.y0}" x2="${mask.x1}" y2="${mask.y1}">
          <stop offset="0" stop-color="white" stop-opacity="${s0}" />
          <stop offset="1" stop-color="white" stop-opacity="${s1}" />
        </linearGradient>
      </defs>
      <rect width="1" height="1" fill="url(#m)" />
    `;
  } else {
    return null;
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1' preserveAspectRatio='none'>${content}</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

// Merge the global adjustments with the mask's local deltas so the
// masked layer renders "global + local". Only the numeric slider
// keys are added — HSL / ToneCurve / Detail already live at the
// global level and shouldn't double up.
function mergeMaskAdjustments(baseAdj, maskAdj) {
  if (!maskAdj) return baseAdj;
  const keys = [
    "Exposure2012", "Contrast2012",
    "Highlights2012", "Shadows2012",
    "Whites2012", "Blacks2012",
    "Temperature", "Tint",
    "Saturation", "Clarity2012",
  ];
  const out = { ...baseAdj };
  for (const k of keys) {
    out[k] = (baseAdj[k] || 0) + (maskAdj[k] || 0);
  }
  return out;
}

export default function RawEditPanel({ photo, isOpen, onClose, groupSize = 0 }) {
  const [adj, setAdj] = useState(DEFAULT_ADJUSTMENTS);
  const [hsl, setHsl] = useState(DEFAULT_HSL);
  const [curve, setCurve] = useState(DEFAULT_CURVE);
  const [mask, setMask] = useState(DEFAULT_MASK);
  const [saving, setSaving] = useState(false);
  const [applyToGroup, setApplyToGroup] = useState(false);
  const [exportState, setExportState] = useState(null); // {status,cdn_url,error}
  const [activeTab, setActiveTab] = useState("basic"); // basic | curves | hsl | detail | masks
  // Phase 98: Alt-drag clipping preview. Track the Alt key + which
  // slider is currently being interacted with, and swap the preview
  // filter chain when the two align.
  const altHeld = useAltKey();
  const [activeSlider, setActiveSlider] = useState(null);
  // Rehydrate on open. Phase 97: also fetch the freshest photo doc
  // from the backend so we pick up XMP edits another editor made
  // after the current user's session list was cached in memory.
  useEffect(() => {
    if (!isOpen || !photo?.id) return;
    let cancelled = false;
    const applyAdjustments = (a) => {
      setAdj({ ...DEFAULT_ADJUSTMENTS, ...a });
      setHsl({ ...DEFAULT_HSL, ...(a.HSL || {}) });
      setCurve(Array.isArray(a.ToneCurvePV2012) ? a.ToneCurvePV2012 : DEFAULT_CURVE);
      setMask(a.Mask && (a.Mask.type === "radial" || a.Mask.type === "linear")
        ? a.Mask : DEFAULT_MASK);
    };
    // 1) Instant paint from whatever the parent already handed us so
    //    the panel feels snappy — no visible flash.
    if (photo?.raw_edit_adjustments) {
      applyAdjustments(photo.raw_edit_adjustments);
    } else {
      setAdj(DEFAULT_ADJUSTMENTS);
      setHsl(DEFAULT_HSL);
      setCurve(DEFAULT_CURVE);
      setMask(DEFAULT_MASK);
    }
    setApplyToGroup(false);
    setExportState(null);
    // 2) Refresh from the DB so we honour edits saved elsewhere.
    (async () => {
      try {
        const { data: fresh } = await axios.get(`${API}/photos/${photo.id}`);
        if (cancelled) return;
        if (fresh?.raw_edit_adjustments) {
          applyAdjustments(fresh.raw_edit_adjustments);
        }
      } catch {
        // Network hiccup — the instant-paint state is fine to keep.
      }
    })();
    return () => { cancelled = true; };
    // Deliberately omit ``photo.raw_edit_adjustments`` — re-running
    // the effect when the parent's cached adjustments object updates
    // would nuke the user's in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, photo?.id]);

  if (!isOpen || !photo) return null;

  const previewUrl = photo.preview_cdn_url || photo.cdn_url;
  const filterId = `pp-raw-${photo.id.slice(0, 8)}`;
  // Fold the HSL sub-state into the payload we feed the filter
  // builders so hue/sat/light shifts show up on the preview too.
  const adjWithHsl = { ...adj, HSL: hsl };
  const tempTintUrl = buildTempTintSvg(adjWithHsl, filterId);
  const curveFilterId = `${filterId}-c`;
  const curveFilterUrl = buildToneCurveSvg(curve, curveFilterId);
  // Phase 98: Alt-drag → clipping preview. When Alt is held AND a
  // tonal slider is being dragged, append a threshold filter that
  // masks everything except the highlight- or shadow-clipping pixels.
  //
  // Phase 98.1: HSL sub-sliders show up with a synthetic key
  // ``HSL:<channel>:<h|s|l>``; all of them share the "grayscale"
  // preview so you can gauge luminance impact without the colour
  // fighting for attention.
  let clipMode = null;
  if (altHeld && activeSlider) {
    if (activeSlider.startsWith("HSL:")) clipMode = "grayscale";
    else clipMode = SLIDER_CLIP_MODE[activeSlider] || null;
  }
  const clipUrl = clipMode
    ? buildClipPreviewSvg(clipMode, `${filterId}-clip`)
    : null;
  const cssFilter = adjustmentsToCssFilter(adjWithHsl)
    + ` url("${tempTintUrl}")`
    + (curveFilterUrl ? ` url("${curveFilterUrl}")` : "")
    + (clipUrl ? ` url("${clipUrl}")` : "");

  // Phase 96: preview-layer for masks — CSS-mask a second img with
  // the merged (globals + mask locals) filter on top of the base.
  const maskFilterId = `${filterId}-m`;
  const mergedAdjForMask = { ...mergeMaskAdjustments(adj, mask?.adjustments), HSL: hsl };
  const maskTempTintUrl = buildTempTintSvg(mergedAdjForMask, maskFilterId);
  const maskCssFilter = adjustmentsToCssFilter(mergedAdjForMask)
    + ` url("${maskTempTintUrl}")`
    + (curveFilterUrl ? ` url("${curveFilterUrl}")` : "");
  const maskDataUrl = buildMaskDataUrl(mask);

  const setKey = (k, v) => setAdj((prev) => ({ ...prev, [k]: v }));
  const setHslKey = (ch, kind, v) => setHsl((prev) => ({
    ...prev,
    [ch]: { ...(prev[ch] || { h: 0, s: 0, l: 0 }), [kind]: v },
  }));
  const setMaskAdj = (k, v) => setMask((prev) => (
    prev ? { ...prev, adjustments: { ...prev.adjustments, [k]: v } } : prev
  ));
  const resetAll = () => {
    setAdj(DEFAULT_ADJUSTMENTS);
    setHsl(DEFAULT_HSL);
    setCurve(DEFAULT_CURVE);
    setMask(DEFAULT_MASK);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/photos/${photo.id}/raw-edit`,
        {
          adjustments: { ...adj, HSL: hsl, ToneCurvePV2012: curve, Mask: mask },
          apply_to_group: applyToGroup,
        },
      );
      toast.success(
        applyToGroup
          ? `XMP written to ${data.applied_count} photo${data.applied_count === 1 ? "" : "s"} in this group`
          : "Edit settings saved as XMP",
        {
          duration: 4500,
          action: {
            label: "Download XMP",
            onClick: () => window.open(data.xmp_url, "_blank"),
          },
        },
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save edit");
    } finally {
      setSaving(false);
    }
  };

  // Phase 93: fire a darktable full-res JPEG render, then poll for
  // completion. Save the XMP first so darktable reads the latest
  // settings.
  const exportJpeg = async () => {
    setExportState({ status: "starting" });
    try {
      // 1) Persist the latest XMP so darktable renders with it.
      await axios.post(`${API}/photos/${photo.id}/raw-edit`, {
        adjustments: { ...adj, HSL: hsl, ToneCurvePV2012: curve, Mask: mask },
        apply_to_group: false,
      });
      // 2) Kick off the render.
      const { data } = await axios.post(`${API}/photos/${photo.id}/export-jpeg`);
      const exportId = data.export_id;
      setExportState({ status: "running" });
      // 3) Poll every 2s until done or 3 min timeout.
      const started = Date.now();
      while (Date.now() - started < 180_000) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: st } = await axios.get(`${API}/photos/exports/${exportId}`);
        if (st.status === "complete") {
          setExportState(st);
          toast.success(`Full-res JPEG ready · ${(st.size / 1_048_576).toFixed(1)} MB`, {
            duration: 6000,
            action: { label: "Download", onClick: () => window.open(st.cdn_url, "_blank") },
          });
          return;
        }
        if (st.status === "failed") {
          setExportState(st);
          toast.error(`Render failed: ${st.error || "unknown error"}`);
          return;
        }
      }
      setExportState({ status: "failed", error: "Timed out after 3 min" });
      toast.error("Render timed out after 3 min — try again or check server logs");
    } catch (err) {
      setExportState({ status: "failed", error: String(err?.message || err) });
      toast.error(err?.response?.data?.detail || "Failed to start export");
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] bg-black/70 backdrop-blur-sm p-[1%] flex items-center justify-center"
      data-testid="raw-edit-panel"
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
      tabIndex={-1}
      // Radix's <Dialog modal={true}> (used by the compare/detail
      // dialog we open FROM) puts ``pointer-events: none`` on <body>
      // while it's open, so anything outside its portalled content
      // silently drops clicks. This inline override wins for the
      // whole overlay tree.
      style={{ pointerEvents: "auto" }}
    >
      <div className="bg-slate-950 text-slate-100 rounded-lg w-[98vw] h-[98vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
              XMP Settings Panel
            </span>
            <span className="text-xs text-slate-300 truncate">
              {photo.filename}
            </span>
            {photo.is_raw && (
              <span className="text-[10px] font-mono text-amber-400 shrink-0">
                RAW · preview-approximated
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-slate-700 text-slate-300 text-[11px] font-bold uppercase tracking-widest hover:bg-slate-800"
              title="Clear all sliders on this photo. Click Save XMP afterwards to persist the reset (leave Apply-to-Group unchecked to reset THIS photo only)."
              data-testid="raw-edit-reset"
            >
              <RotateCcw className="h-3 w-3" />
              Reset
            </button>
            {/* Apply-to-group affordance. Always rendered so it is
                discoverable; disabled when there are no siblings to
                apply to (single-photo group). Phase 97. */}
            <label
              className={`inline-flex items-center gap-1.5 h-8 px-2 rounded-sm border text-[11px] font-bold uppercase tracking-widest transition-colors ${
                groupSize > 1
                  ? (applyToGroup
                      ? "border-primary bg-primary/20 text-white cursor-pointer"
                      : "border-slate-700 text-slate-300 hover:bg-slate-800 cursor-pointer")
                  : "border-slate-800 text-slate-600 cursor-not-allowed opacity-60"
              }`}
              title={groupSize > 1
                ? "When checked, Save XMP writes these settings to every photo in this group"
                : "Only one photo in this group — nothing to apply to"}
            >
              <input
                type="checkbox"
                checked={applyToGroup}
                onChange={(e) => setApplyToGroup(e.target.checked)}
                disabled={groupSize <= 1}
                className="h-3 w-3 accent-primary"
                data-testid="raw-edit-apply-to-group"
              />
              <Users className="h-3 w-3" />
              Apply to group{groupSize > 1 ? ` (${groupSize - 1})` : ""}
            </label>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-sm bg-primary text-white text-[11px] font-bold uppercase tracking-widest hover:bg-primary/90 disabled:opacity-60"
              data-testid="raw-edit-save"
            >
              <Save className="h-3 w-3" />
              {saving ? "Saving…" : "Save XMP"}
            </button>
            {/* Phase 93: full-res JPEG render via darktable-cli. */}
            <button
              type="button"
              onClick={exportJpeg}
              disabled={!!exportState && exportState.status === "running"}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-sm bg-emerald-600 text-white text-[11px] font-bold uppercase tracking-widest hover:bg-emerald-700 disabled:opacity-60"
              data-testid="raw-edit-export-jpeg"
              title="Render CR2 + XMP → full-resolution JPEG via darktable (30-120s)"
            >
              <Download className="h-3 w-3" />
              {exportState?.status === "running" || exportState?.status === "starting"
                ? "Rendering…"
                : exportState?.status === "complete"
                  ? "Ready →"
                  : "Export JPEG"}
            </button>
            {exportState?.status === "complete" && (
              <a
                href={exportState.cdn_url}
                download
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-emerald-600 text-emerald-400 text-[11px] font-bold uppercase tracking-widest hover:bg-emerald-950"
                data-testid="raw-edit-download-jpeg"
              >
                <Download className="h-3 w-3" />
                JPEG
              </a>
            )}
            {photo.cdn_url && (
              <a
                href={photo.cdn_url}
                download
                className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-slate-700 text-slate-300 text-[11px] font-bold uppercase tracking-widest hover:bg-slate-800"
                data-testid="raw-edit-download-cr2"
                title="Download the original CR2"
              >
                <Download className="h-3 w-3" />
                CR2
              </a>
            )}
            {photo.cdn_url && (
              <a
                href={photo.cdn_url.replace(/\.[^.\/]+(\?.*)?$/, ".xmp$1")}
                download
                className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-slate-700 text-slate-300 text-[11px] font-bold uppercase tracking-widest hover:bg-slate-800"
                data-testid="raw-edit-download-xmp"
                title="Download the XMP sidecar (Save first to include your latest edits)"
              >
                <Download className="h-3 w-3" />
                XMP
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="raw-edit-close"
              title="Close (Esc)"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body: image | sliders */}
        <div className="flex-1 flex overflow-hidden">
          {/* Preview */}
          <div className="flex-1 overflow-hidden bg-black relative">
            <PreviewWithMask
              src={previewUrl}
              alt={photo.filename}
              cssFilter={cssFilter}
              maskCssFilter={maskCssFilter}
              maskDataUrl={maskDataUrl}
              mask={mask}
              onMaskChange={setMask}
              overlayActive={activeTab === "masks"}
            />
            {/* Phase 98: badge appears whenever the Alt clipping
                preview is active so the user knows why the image
                just went almost-black / almost-white. */}
            {clipMode && (
              <div
                className="absolute top-3 left-3 flex items-center gap-1.5 h-7 px-2.5 rounded-sm bg-amber-500/95 text-slate-950 text-[10px] font-bold uppercase tracking-widest shadow-lg pointer-events-none"
                data-testid="raw-edit-clip-indicator"
              >
                Alt · {CLIP_MODE_LABEL[clipMode] || clipMode}
              </div>
            )}
          </div>

          {/* Sliders panel */}
          <div
            className="w-[340px] shrink-0 border-l border-slate-800 overflow-y-auto flex flex-col"
            data-testid="raw-edit-sliders"
          >
            {/* Phase 97: RGB histogram, computed from the preview
                image on load. Sits above the tab strip so it's
                always visible while editing. */}
            <Histogram src={previewUrl} />
            {/* Tab strip */}
            <div className="flex border-b border-slate-800 shrink-0">
              {[
                { id: "basic", label: "Basic" },
                { id: "curves", label: "Curves" },
                { id: "hsl", label: "HSL" },
                { id: "detail", label: "Detail" },
                { id: "masks", label: "Masks" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  className={`flex-1 h-9 text-[10px] uppercase tracking-widest font-bold transition-colors ${
                    activeTab === t.id
                      ? "text-white border-b-2 border-primary bg-slate-900"
                      : "text-slate-500 hover:text-slate-300 hover:bg-slate-900/50"
                  }`}
                  data-testid={`raw-edit-tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto">
            {activeTab === "basic" && ["Light", "Colour", "Presence"].map((section) => (
              <div key={section} className="border-b border-slate-800 py-3 px-4">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                  {section}
                </p>
                {SLIDER_CONFIG.filter((s) => s.section === section).map((s) => (
                  <div key={s.key} className="mb-2.5">
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <span className="text-slate-400">{s.label}</span>
                      <button
                        type="button"
                        onClick={() => setKey(s.key, DEFAULT_ADJUSTMENTS[s.key])}
                        className="font-mono tabular-nums text-slate-200 hover:text-white text-[11px]"
                        title="Click to reset"
                        data-testid={`raw-edit-value-${s.key}`}
                      >
                        {typeof adj[s.key] === "number"
                          ? (s.step < 1
                            ? (adj[s.key] > 0 ? "+" : "") + adj[s.key].toFixed(2)
                            : (adj[s.key] > 0 ? "+" : "") + Math.round(adj[s.key]))
                          : adj[s.key]}
                      </button>
                    </div>
                    <input
                      type="range"
                      min={s.min}
                      max={s.max}
                      step={s.step}
                      value={adj[s.key]}
                      onChange={(e) => setKey(s.key, parseFloat(e.target.value))}
                      onMouseDown={() => setActiveSlider(s.key)}
                      onMouseUp={() => setActiveSlider(null)}
                      onKeyDown={() => setActiveSlider(s.key)}
                      onKeyUp={() => setActiveSlider(null)}
                      onDoubleClick={() => setKey(s.key, DEFAULT_ADJUSTMENTS[s.key])}
                      className="w-full accent-primary"
                      data-testid={`raw-edit-slider-${s.key}`}
                    />
                  </div>
                ))}
              </div>
            ))}

            {activeTab === "curves" && (
              <div className="p-4">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                  Tone Curve
                </p>
                <ToneCurveEditor curve={curve} onChange={setCurve} />
                <button
                  type="button"
                  onClick={() => setCurve(DEFAULT_CURVE)}
                  className="mt-2 text-[10px] uppercase tracking-widest text-slate-500 hover:text-white"
                  data-testid="raw-edit-curve-reset"
                >
                  Reset to identity
                </button>
                <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
                  Click on the curve to add a point; drag to shape the tone
                  response. Full pipeline is applied on JPEG export via
                  darktable.
                </p>
              </div>
            )}

            {activeTab === "hsl" && (
              <div className="p-4 space-y-4">
                {["h", "s", "l"].map((kind) => (
                  <div key={kind}>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                      {kind === "h" ? "Hue" : kind === "s" ? "Saturation" : "Luminance"}
                    </p>
                    {HSL_CHANNELS.map((c) => (
                      <div key={c.key} className="mb-2">
                        <div className="flex items-center justify-between text-[11px] mb-1">
                          <span className="inline-flex items-center gap-1.5 text-slate-300">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.tint }} />
                            {c.key}
                          </span>
                          <button
                            type="button"
                            onClick={() => setHslKey(c.key, kind, 0)}
                            className="font-mono tabular-nums text-slate-200 hover:text-white"
                            data-testid={`raw-edit-hsl-${c.key}-${kind}-value`}
                          >
                            {((hsl[c.key] || {})[kind] || 0) > 0 ? "+" : ""}
                            {(hsl[c.key] || {})[kind] || 0}
                          </button>
                        </div>
                        <input
                          type="range"
                          min={-100} max={100} step={1}
                          value={(hsl[c.key] || {})[kind] || 0}
                          onChange={(e) => setHslKey(c.key, kind, parseInt(e.target.value, 10))}
                          onMouseDown={() => setActiveSlider(`HSL:${c.key}:${kind}`)}
                          onMouseUp={() => setActiveSlider(null)}
                          onKeyDown={() => setActiveSlider(`HSL:${c.key}:${kind}`)}
                          onKeyUp={() => setActiveSlider(null)}
                          onDoubleClick={() => setHslKey(c.key, kind, 0)}
                          className="w-full accent-primary"
                          data-testid={`raw-edit-hsl-${c.key}-${kind}`}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {activeTab === "detail" && ["Sharpening", "Noise Reduction · Luminance", "Noise Reduction · Colour"].map((section) => (
              <div key={section} className="border-b border-slate-800 py-3 px-4">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                  {section}
                </p>
                {DETAIL_SLIDER_CONFIG.filter((s) => s.section === section).map((s) => (
                  <div key={s.key} className="mb-2.5">
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <span className="text-slate-400">{s.label}</span>
                      <button
                        type="button"
                        onClick={() => setKey(s.key, DEFAULT_ADJUSTMENTS[s.key])}
                        className="font-mono tabular-nums text-slate-200 hover:text-white text-[11px]"
                        title="Click to reset"
                        data-testid={`raw-edit-value-${s.key}`}
                      >
                        {typeof adj[s.key] === "number"
                          ? (s.step < 1
                            ? adj[s.key].toFixed(1)
                            : Math.round(adj[s.key]))
                          : adj[s.key]}
                      </button>
                    </div>
                    <input
                      type="range"
                      min={s.min}
                      max={s.max}
                      step={s.step}
                      value={adj[s.key] ?? DEFAULT_ADJUSTMENTS[s.key]}
                      onChange={(e) => setKey(s.key, parseFloat(e.target.value))}
                      onMouseDown={() => setActiveSlider(s.key)}
                      onMouseUp={() => setActiveSlider(null)}
                      onKeyDown={() => setActiveSlider(s.key)}
                      onKeyUp={() => setActiveSlider(null)}
                      onDoubleClick={() => setKey(s.key, DEFAULT_ADJUSTMENTS[s.key])}
                      className="w-full accent-primary"
                      data-testid={`raw-edit-slider-${s.key}`}
                    />
                  </div>
                ))}
                {section === "Sharpening" && (
                  <p className="text-[10px] text-slate-500 leading-relaxed mt-1">
                    Amount 0 disables sharpening. Radius controls halo width.
                    Masking hides sharpening from smooth areas (skies, skin).
                  </p>
                )}
              </div>
            ))}

            {activeTab === "masks" && (
              <MasksTab
                mask={mask}
                onCreateRadial={() => setMask(NEW_RADIAL_MASK())}
                onCreateLinear={() => setMask(NEW_LINEAR_MASK())}
                onDelete={() => setMask(DEFAULT_MASK)}
                onToggleInvert={() =>
                  setMask((m) => (m ? { ...m, invert: !m.invert } : m))
                }
                onFeather={(v) =>
                  setMask((m) => (m ? { ...m, feather: v } : m))
                }
                onAdjust={setMaskAdj}
              />
            )}

            <div className="p-4 text-[10px] leading-relaxed text-slate-500">
              Live preview is an approximation. The <b>Export JPEG</b> button
              renders the FINAL image at native resolution using darktable
              (~30–120s per photo).
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}


// Compact tone-curve editor. Renders a 200x200 SVG grid with the
// current curve points as draggable handles. Left-click empty space
// to add a point; right-click a point to remove it.
const CURVE_SIZE = 220;
const CURVE_PAD = 8;
const CURVE_W = CURVE_SIZE - 2 * CURVE_PAD;
const curveToSvg = ([x, y]) => [
  CURVE_PAD + (x / 255) * CURVE_W,
  CURVE_PAD + CURVE_W - (y / 255) * CURVE_W,
];
const curveToDomain = (sx, sy) => [
  Math.max(0, Math.min(255, Math.round(((sx - CURVE_PAD) / CURVE_W) * 255))),
  Math.max(0, Math.min(255, Math.round(255 - ((sy - CURVE_PAD) / CURVE_W) * 255))),
];

function ToneCurveEditor({ curve, onChange }) {
  const size = CURVE_SIZE;
  const pad = CURVE_PAD;
  const w = CURVE_W;

  const [dragIdx, setDragIdx] = useState(null);
  const svgRef = useRef(null);

  const onDown = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    // Phase 98: Alt+click a point → snap it back to identity (y=x).
    // Matches Photoshop's Alt-click reset semantics; if the click
    // was already on an interior point that identity-clicks would
    // remove (endpoints stay put).
    if (e.altKey) {
      const p = curve[idx];
      if (!p) return;
      const next = curve.map((q, i) => (i === idx ? [q[0], q[0]] : q));
      onChange(next.sort((a, b) => a[0] - b[0]));
      return;
    }
    setDragIdx(idx);
  };
  // Window-attached listeners: the SVG is only 220px so drags very
  // easily leave the surface. Using window mousemove + mouseup keeps
  // the interaction alive until the button is released anywhere.
  useEffect(() => {
    if (dragIdx === null) return;
    const onMove = (e) => {
      const el = svgRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const sx = Math.max(CURVE_PAD, Math.min(CURVE_PAD + CURVE_W, e.clientX - rect.left));
      const sy = Math.max(CURVE_PAD, Math.min(CURVE_PAD + CURVE_W, e.clientY - rect.top));
      const [nx, ny] = curveToDomain(sx, sy);
      onChange(
        curve
          .map((p, i) => (i === dragIdx ? [nx, ny] : p))
          .sort((a, b) => a[0] - b[0]),
      );
    };
    const onUp = () => setDragIdx(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragIdx, curve, onChange]);

  const onClickBg = (e) => {
    if (dragIdx !== null) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const [nx, ny] = curveToDomain(e.clientX - rect.left, e.clientY - rect.top);
    const next = [...curve, [nx, ny]].sort((a, b) => a[0] - b[0]);
    onChange(next);
  };
  const onContext = (e, idx) => {
    e.preventDefault();
    if (curve.length <= 2) return;  // keep endpoints
    const next = curve.filter((_, i) => i !== idx);
    onChange(next);
  };

  const pathD = curve
    .map((p, i) => {
      const [x, y] = curveToSvg(p);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  const idPath = `M ${pad} ${pad + w} L ${pad + w} ${pad}`;

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      className="bg-slate-900 rounded-sm border border-slate-800 cursor-crosshair"
      onClick={onClickBg}
      data-testid="raw-edit-curve-svg"
    >
      {/* grid */}
      {[0.25, 0.5, 0.75].map((f) => (
        <g key={f}>
          <line x1={pad + w * f} y1={pad} x2={pad + w * f} y2={pad + w} stroke="#1e293b" />
          <line x1={pad} y1={pad + w * f} x2={pad + w} y2={pad + w * f} stroke="#1e293b" />
        </g>
      ))}
      <path d={idPath} stroke="#334155" strokeDasharray="2 3" fill="none" />
      <path d={pathD} stroke="#0ea5e9" strokeWidth={2} fill="none" />
      {curve.map((p, i) => {
        const [x, y] = curveToSvg(p);
        return (
          <circle
            key={i}
            cx={x} cy={y} r={5}
            fill={dragIdx === i ? "#0ea5e9" : "#f8fafc"}
            stroke="#0ea5e9" strokeWidth={2}
            onMouseDown={(e) => onDown(e, i)}
            onContextMenu={(e) => onContext(e, i)}
            className="cursor-move"
          />
        );
      })}
    </svg>
  );
}



// Phase 94: Preview with an interactive mask overlay drawn on top of
// the image. The mask geometry is stored normalized (0..1) so the same
// numbers work at any display size. The red overlay visualizes WHERE
// the mask affects — the actual pixel-level effect is only visible in
// the darktable-rendered JPEG export.
//
// Phase 96: adds smooth zoom + pan on the whole preview.
//   • wheel = zoom in/out with cursor as the pivot (Lightroom-style)
//   • click-drag on the image = pan (once zoomed)
//   • toolbar: Fit / 100% / -/+
//   • mask handles still work under zoom because both the image and
//     the SVG overlay share the same transform, and coord math is
//     normalized against the SVG's post-transform bounding rect
//     (i.e. the ratio stays 0..1 no matter the zoom level).
function PreviewWithMask({ src, alt, cssFilter, maskCssFilter, maskDataUrl, mask, onMaskChange, overlayActive }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const [maskDrag, setMaskDrag] = useState(null); // {kind, snapshot}

  // Reset viewport when the photo changes.
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [src]);

  const clampZoom = (z) => Math.max(0.1, Math.min(8, z));

  // Zoom towards the cursor so the point under the pointer stays put.
  // Transform is `translate(pan) scale(zoom)` with transform-origin
  // set to the container centre — derivation lives in the commit.
  const zoomAtScreen = (clientX, clientY, newZoom) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) { setZoom(newZoom); return; }
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    setZoom((prevZoom) => {
      const factor = newZoom / prevZoom;
      setPan((p) => ({
        x: (clientX - cx) * (1 - factor) + p.x * factor,
        y: (clientY - cy) * (1 - factor) + p.y * factor,
      }));
      return newZoom;
    });
  };

  // Use a ref so the wheel handler always sees the latest zoom without
  // forcing us to rebind the native listener on every zoom change.
  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  // React's synthetic onWheel is passive, so we can't preventDefault
  // via that pipeline. Bind a native non-passive listener on the
  // outer container so wheel works anywhere in the preview (empty
  // black margins included). Skip when the event started inside the
  // zoom toolbar.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      if (e.target.closest?.("[data-testid='raw-edit-zoom-toolbar']")) return;
      e.preventDefault();
      const step = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      zoomAtScreen(e.clientX, e.clientY, clampZoom(zoomRef.current * step));
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  // Global mousemove/mouseup so drags don't die when the cursor leaves
  // the image (Lightroom / Photoshop behaviour).
  useEffect(() => {
    if (!isPanning && !maskDrag) return;
    const onMove = (e) => {
      if (isPanning) {
        setPan({
          x: panStart.current.px + (e.clientX - panStart.current.mx),
          y: panStart.current.py + (e.clientY - panStart.current.my),
        });
        return;
      }
      if (maskDrag) {
        // Normalize against the img's live bounding rect. This is
        // post-transform, so the ratio is invariant under zoom.
        const rect = imgRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0) return;
        const nx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const ny = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
        const snap = maskDrag.snapshot;
        if (snap.type === "radial") {
          if (maskDrag.kind === "center") {
            onMaskChange({ ...snap, cx: snap.cx + (nx - maskDrag.startNx),
                                    cy: snap.cy + (ny - maskDrag.startNy) });
          } else if (maskDrag.kind === "edge") {
            const rx = Math.max(0.02, Math.abs(nx - snap.cx));
            const ry = Math.max(0.02, Math.abs(ny - snap.cy));
            onMaskChange({ ...snap, rx, ry });
          }
        } else if (snap.type === "linear") {
          if (maskDrag.kind === "p0") onMaskChange({ ...snap, x0: nx, y0: ny });
          else if (maskDrag.kind === "p1") onMaskChange({ ...snap, x1: nx, y1: ny });
        }
      }
    };
    const onUp = () => { setIsPanning(false); setMaskDrag(null); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isPanning, maskDrag, onMaskChange]);

  // Called by MaskOverlay handles.
  const beginMaskDrag = (kind, e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const startNx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const startNy = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    setMaskDrag({ kind, startNx, startNy, snapshot: { ...mask } });
  };

  const beginPan = (e) => {
    // Left button only; ignore right-click / middle-click.
    if (e.button !== 0) return;
    panStart.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
    setIsPanning(true);
  };

  const onDoubleClick = () => {
    // Toggle fit ↔ 100%.
    if (Math.abs(zoom - 1) < 0.001) {
      const img = imgRef.current;
      if (img && img.clientWidth) {
        const ratio = img.naturalWidth / img.clientWidth;
        setZoom(clampZoom(ratio));
        setPan({ x: 0, y: 0 });
      }
    } else {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
  };

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex items-center justify-center overflow-hidden bg-black relative select-none"
      onMouseDown={beginPan}
      onDoubleClick={onDoubleClick}
      data-testid="raw-edit-preview-viewport"
      style={{ cursor: isPanning ? "grabbing" : (zoom > 1 ? "grab" : "default") }}
    >
      <div
        className="relative inline-block"
        style={{
          transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom})`,
          transformOrigin: "center center",
          transition: isPanning || maskDrag ? "none" : "transform 120ms ease-out",
          willChange: "transform",
        }}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          draggable={false}
          style={{
            filter: cssFilter,
            maxWidth: "100%",
            maxHeight: "100%",
            display: "block",
            pointerEvents: "none",
          }}
          data-testid="raw-edit-preview"
        />
        {/* Phase 96: stacked WYSIWYG layer for the local mask. Same
            image, CSS-masked to the mask's shape, with the merged
            (globals + mask locals) filter applied. When there's no
            mask, this element is null and only the base renders. */}
        {mask && maskDataUrl && (
          <img
            src={src}
            alt=""
            draggable={false}
            aria-hidden="true"
            style={{
              filter: maskCssFilter,
              maxWidth: "100%",
              maxHeight: "100%",
              display: "block",
              pointerEvents: "none",
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              WebkitMaskImage: `url("${maskDataUrl}")`,
              maskImage: `url("${maskDataUrl}")`,
              WebkitMaskSize: "100% 100%",
              maskSize: "100% 100%",
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
            }}
            data-testid="raw-edit-mask-preview-layer"
          />
        )}
        {mask && (
          <MaskOverlay
            mask={mask}
            imgRef={imgRef}
            active={overlayActive}
            onBeginDrag={beginMaskDrag}
          />
        )}
      </div>

      {/* Zoom toolbar. Absolutely positioned so it doesn't disrupt the
          centred image. Pointer-events lifted so it isn't sucked into
          the pan handler on the viewport. */}
      <div
        className="absolute bottom-3 left-3 flex items-center gap-1 bg-slate-900/80 border border-slate-700 rounded-sm backdrop-blur-sm p-1"
        onMouseDown={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        data-testid="raw-edit-zoom-toolbar"
      >
        <button
          type="button"
          onClick={() => zoomAtScreen(
            containerRef.current.getBoundingClientRect().left + containerRef.current.clientWidth / 2,
            containerRef.current.getBoundingClientRect().top + containerRef.current.clientHeight / 2,
            clampZoom(zoom / 1.25),
          )}
          className="h-7 w-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-800 text-slate-300"
          title="Zoom out"
          data-testid="raw-edit-zoom-out"
        >
          <ZoomOut className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
          className="h-7 px-2 inline-flex items-center justify-center rounded-sm hover:bg-slate-800 text-slate-300 text-[10px] font-mono tabular-nums"
          title="Fit to screen"
          data-testid="raw-edit-zoom-fit"
        >
          <Maximize className="h-3 w-3 mr-1" />
          Fit
        </button>
        <div className="text-[10px] font-mono tabular-nums text-slate-400 min-w-[38px] text-center">
          {Math.round(zoom * 100)}%
        </div>
        <button
          type="button"
          onClick={() => {
            const img = imgRef.current;
            if (img && img.clientWidth) {
              setZoom(clampZoom(img.naturalWidth / img.clientWidth));
              setPan({ x: 0, y: 0 });
            }
          }}
          className="h-7 px-2 inline-flex items-center justify-center rounded-sm hover:bg-slate-800 text-slate-300 text-[10px] font-mono"
          title="Zoom to 100%"
          data-testid="raw-edit-zoom-100"
        >
          1:1
        </button>
        <button
          type="button"
          onClick={() => zoomAtScreen(
            containerRef.current.getBoundingClientRect().left + containerRef.current.clientWidth / 2,
            containerRef.current.getBoundingClientRect().top + containerRef.current.clientHeight / 2,
            clampZoom(zoom * 1.25),
          )}
          className="h-7 w-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-800 text-slate-300"
          title="Zoom in"
          data-testid="raw-edit-zoom-in"
        >
          <ZoomIn className="h-3.5 w-3.5" />
        </button>
        {zoom > 1 && (
          <span className="pl-2 pr-1 text-[9px] uppercase tracking-widest text-slate-500 inline-flex items-center gap-1">
            <Move className="h-3 w-3" />
            drag to pan
          </span>
        )}
      </div>
    </div>
  );
}

// SVG overlay for the current mask. Handles are pointer-events: auto;
// everything else is pointer-events: none so pan / wheel bubble up.
// Drag continuation is done via window listeners in PreviewWithMask,
// so cursor leaving the SVG mid-drag doesn't drop the interaction.
function MaskOverlay({ mask, imgRef, active, onBeginDrag }) {
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const measure = () => setDims({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [imgRef]);

  if (!dims.w || !dims.h) return null;
  const { w, h } = dims;
  const overlayAlpha = active ? 0.45 : 0;
  const outlineColor = "#f43f5e";
  const outlineWidth = active ? 2 : 1.5;

  return (
    <svg
      width={w}
      height={h}
      className="absolute top-0 left-0"
      style={{ pointerEvents: "none" }}
      data-testid="raw-edit-mask-overlay"
    >
      <defs>
        {mask.type === "radial" && (
          <radialGradient
            id="pp-mask-radial"
            cx={mask.cx} cy={mask.cy}
            r={Math.max(mask.rx, mask.ry)}
            fx={mask.cx} fy={mask.cy}
            gradientUnits="objectBoundingBox"
          >
            <stop offset="0" stopColor="#f43f5e"
              stopOpacity={mask.invert ? 0 : overlayAlpha} />
            <stop offset={Math.max(0.01, 1 - (mask.feather ?? 0.5))}
              stopColor="#f43f5e"
              stopOpacity={mask.invert ? 0 : overlayAlpha * 0.75} />
            <stop offset="1" stopColor="#f43f5e"
              stopOpacity={mask.invert ? overlayAlpha : 0} />
          </radialGradient>
        )}
        {mask.type === "linear" && (
          <linearGradient
            id="pp-mask-linear"
            x1={mask.x0} y1={mask.y0} x2={mask.x1} y2={mask.y1}
            gradientUnits="objectBoundingBox"
          >
            <stop offset="0" stopColor="#f43f5e"
              stopOpacity={mask.invert ? 0 : overlayAlpha} />
            <stop offset="1" stopColor="#f43f5e"
              stopOpacity={mask.invert ? overlayAlpha : 0} />
          </linearGradient>
        )}
      </defs>

      {active && mask.type === "radial" && (
        <rect x="0" y="0" width={w} height={h} fill="url(#pp-mask-radial)" />
      )}
      {active && mask.type === "linear" && (
        <rect x="0" y="0" width={w} height={h} fill="url(#pp-mask-linear)" />
      )}

      {mask.type === "radial" && (
        <>
          <ellipse
            cx={mask.cx * w}
            cy={mask.cy * h}
            rx={mask.rx * w}
            ry={mask.ry * h}
            fill="none"
            stroke={outlineColor}
            strokeWidth={outlineWidth}
            strokeDasharray="4 3"
          />
          {active && (
            <>
              <circle
                cx={mask.cx * w} cy={mask.cy * h} r={8}
                fill="#f43f5e" stroke="white" strokeWidth={2}
                style={{ cursor: "move", pointerEvents: "auto" }}
                onMouseDown={(e) => onBeginDrag("center", e)}
                data-testid="raw-edit-mask-radial-center"
              />
              <circle
                cx={(mask.cx + mask.rx) * w} cy={mask.cy * h} r={6}
                fill="white" stroke="#f43f5e" strokeWidth={2}
                style={{ cursor: "ew-resize", pointerEvents: "auto" }}
                onMouseDown={(e) => onBeginDrag("edge", e)}
                data-testid="raw-edit-mask-radial-edge"
              />
            </>
          )}
        </>
      )}
      {mask.type === "linear" && (
        <>
          <line
            x1={mask.x0 * w} y1={mask.y0 * h}
            x2={mask.x1 * w} y2={mask.y1 * h}
            stroke={outlineColor}
            strokeWidth={outlineWidth}
            strokeDasharray="4 3"
          />
          {(() => {
            const dx = mask.x1 - mask.x0, dy = mask.y1 - mask.y0;
            const len = Math.hypot(dx * w, dy * h) || 1;
            const nx = (-dy * h) / len, ny = (dx * w) / len;
            const guideLen = Math.min(w, h) * 0.35;
            return (
              <>
                <line
                  x1={mask.x0 * w - nx * guideLen} y1={mask.y0 * h - ny * guideLen}
                  x2={mask.x0 * w + nx * guideLen} y2={mask.y0 * h + ny * guideLen}
                  stroke={outlineColor} strokeWidth={outlineWidth * 0.8}
                />
                <line
                  x1={mask.x1 * w - nx * guideLen} y1={mask.y1 * h - ny * guideLen}
                  x2={mask.x1 * w + nx * guideLen} y2={mask.y1 * h + ny * guideLen}
                  stroke={outlineColor} strokeWidth={outlineWidth * 0.8}
                  strokeDasharray="2 3"
                />
              </>
            );
          })()}
          {active && (
            <>
              <circle
                cx={mask.x0 * w} cy={mask.y0 * h} r={8}
                fill="#f43f5e" stroke="white" strokeWidth={2}
                style={{ cursor: "move", pointerEvents: "auto" }}
                onMouseDown={(e) => onBeginDrag("p0", e)}
                data-testid="raw-edit-mask-linear-p0"
              />
              <circle
                cx={mask.x1 * w} cy={mask.y1 * h} r={8}
                fill="white" stroke="#f43f5e" strokeWidth={2}
                style={{ cursor: "move", pointerEvents: "auto" }}
                onMouseDown={(e) => onBeginDrag("p1", e)}
                data-testid="raw-edit-mask-linear-p1"
              />
            </>
          )}
        </>
      )}
    </svg>
  );
}

// Right-side panel content for the "Masks" tab.
function MasksTab({
  mask, onCreateRadial, onCreateLinear, onDelete,
  onToggleInvert, onFeather, onAdjust,
}) {
  return (
    <div className="p-4 space-y-4">
      {/* Type selector */}
      <div>
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
          Mask
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCreateRadial}
            className={`flex-1 h-10 inline-flex items-center justify-center gap-1.5 rounded-sm border text-[11px] font-bold uppercase tracking-widest transition-colors ${
              mask?.type === "radial"
                ? "border-rose-500 bg-rose-950/40 text-rose-300"
                : "border-slate-700 text-slate-400 hover:bg-slate-900"
            }`}
            data-testid="raw-edit-mask-create-radial"
          >
            <Circle className="h-3.5 w-3.5" />
            Radial
          </button>
          <button
            type="button"
            onClick={onCreateLinear}
            className={`flex-1 h-10 inline-flex items-center justify-center gap-1.5 rounded-sm border text-[11px] font-bold uppercase tracking-widest transition-colors ${
              mask?.type === "linear"
                ? "border-rose-500 bg-rose-950/40 text-rose-300"
                : "border-slate-700 text-slate-400 hover:bg-slate-900"
            }`}
            data-testid="raw-edit-mask-create-linear"
          >
            <Slash className="h-3.5 w-3.5" />
            Linear
          </button>
        </div>
        {!mask && (
          <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
            Pick a mask type to add a local adjustment. Drag the handles on the preview to shape it.
          </p>
        )}
      </div>

      {mask && (
        <>
          <div className="flex items-center justify-between border-t border-slate-800 pt-3">
            <label className="inline-flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={!!mask.invert}
                onChange={onToggleInvert}
                className="h-3 w-3 accent-primary"
                data-testid="raw-edit-mask-invert"
              />
              Invert mask
            </label>
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-slate-700 text-slate-400 text-[10px] font-bold uppercase tracking-widest hover:bg-red-950 hover:text-red-300 hover:border-red-800"
              data-testid="raw-edit-mask-delete"
            >
              <Trash2 className="h-3 w-3" />
              Remove
            </button>
          </div>

          {mask.type === "radial" && (
            <div>
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="text-slate-400">Feather</span>
                <button
                  type="button"
                  onClick={() => onFeather(0.5)}
                  className="font-mono tabular-nums text-slate-200 hover:text-white"
                  data-testid="raw-edit-mask-feather-value"
                >
                  {Math.round((mask.feather ?? 0.5) * 100)}
                </button>
              </div>
              <input
                type="range"
                min={0} max={1} step={0.01}
                value={mask.feather ?? 0.5}
                onChange={(e) => onFeather(parseFloat(e.target.value))}
                className="w-full accent-primary"
                data-testid="raw-edit-mask-feather"
              />
            </div>
          )}

          {/* Per-mask local adjustments */}
          <div className="border-t border-slate-800 pt-3">
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
              Local adjustments
            </p>
            {MASK_SLIDER_CONFIG.map((s) => (
              <div key={s.key} className="mb-2.5">
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="text-slate-400">{s.label}</span>
                  <button
                    type="button"
                    onClick={() => onAdjust(s.key, 0)}
                    className="font-mono tabular-nums text-slate-200 hover:text-white text-[11px]"
                    title="Click to reset"
                    data-testid={`raw-edit-mask-value-${s.key}`}
                  >
                    {(() => {
                      const v = mask.adjustments?.[s.key] ?? 0;
                      if (s.step < 1) return (v > 0 ? "+" : "") + v.toFixed(2);
                      return (v > 0 ? "+" : "") + Math.round(v);
                    })()}
                  </button>
                </div>
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  value={mask.adjustments?.[s.key] ?? 0}
                  onChange={(e) => onAdjust(s.key, parseFloat(e.target.value))}
                  onDoubleClick={() => onAdjust(s.key, 0)}
                  className="w-full accent-primary"
                  data-testid={`raw-edit-mask-slider-${s.key}`}
                />
              </div>
            ))}
          </div>

          <p className="text-[10px] text-slate-500 leading-relaxed">
            Mask geometry and adjustments are saved into the XMP sidecar. Full
            per-mask rendering is applied on the darktable JPEG export.
          </p>
        </>
      )}
    </div>
  );
}


// Phase 97: RGB histogram for the RAW editor.
//
// Draws a 320×72 SVG showing per-channel (R/G/B) distributions of the
// preview image. Computed once on load via a hidden canvas — cheap
// enough to keep in the top of the sliders column at all times.
//
// Live filter feedback is intentionally NOT tracked here — CSS
// ``filter`` is applied at compositor level so ``getImageData`` on
// the img would still return the un-filtered pixels. Re-doing the
// filter math in JS would be a ~50x slowdown for very marginal
// value; showing the raw histogram matches Lightroom's classic
// pre-processing view.
function Histogram({ src }) {
  const [bins, setBins] = useState(null); // {r,g,b} → Uint32Array(256)
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!src) return;
    setBins(null);
    setErr(null);
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      try {
        // Downsample: 240×160 is enough resolution for a clean
        // histogram while keeping getImageData in the sub-ms range
        // for a 24MP preview.
        const W = 240, H = 160;
        const c = document.createElement("canvas");
        c.width = W; c.height = H;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, W, H);
        const data = ctx.getImageData(0, 0, W, H).data;
        const r = new Uint32Array(256);
        const g = new Uint32Array(256);
        const b = new Uint32Array(256);
        for (let i = 0; i < data.length; i += 4) {
          r[data[i]]++;
          g[data[i + 1]]++;
          b[data[i + 2]]++;
        }
        if (!cancelled) setBins({ r, g, b });
      } catch (e) {
        // Cross-origin canvas taint → CORS on the CDN blocked us.
        if (!cancelled) setErr("cors");
      }
    };
    img.onerror = () => { if (!cancelled) setErr("load"); };
    img.src = src;
    return () => { cancelled = true; };
  }, [src]);

  const W = 320, H = 72, PAD = 4;
  const plotW = W - PAD * 2;
  const plotH = H - PAD * 2;

  // Phase 97: hide the whole panel when the CDN blocks canvas reads
  // (CORS) or the image fails to load. An empty box was more
  // confusing than useful.
  if (err) return null;

  const path = (channel) => {
    if (!bins || !bins[channel]) return "";
    const arr = bins[channel];
    // Log-compress so highlight spikes don't flatten the mids.
    const log = new Float32Array(256);
    let max = 0;
    for (let i = 0; i < 256; i++) {
      log[i] = Math.log1p(arr[i]);
      if (log[i] > max) max = log[i];
    }
    if (max === 0) return "";
    let d = `M ${PAD} ${PAD + plotH}`;
    for (let i = 0; i < 256; i++) {
      const x = PAD + (i / 255) * plotW;
      const y = PAD + plotH - (log[i] / max) * plotH;
      d += ` L ${x.toFixed(1)} ${y.toFixed(1)}`;
    }
    d += ` L ${PAD + plotW} ${PAD + plotH} Z`;
    return d;
  };

  return (
    <div className="px-3 pt-3 pb-2 border-b border-slate-800 shrink-0" data-testid="raw-edit-histogram">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
          Histogram
        </p>
        {err && (
          <span className="text-[9px] text-slate-600 italic" data-testid="raw-edit-histogram-error">
            {err === "cors" ? "CDN blocked · pre-edit only" : "load failed"}
          </span>
        )}
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="w-full bg-slate-950 rounded-sm border border-slate-800"
        style={{ display: "block", height: H }}
        data-testid="raw-edit-histogram-svg"
      >
        {/* Grid: quartiles. */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1={PAD + plotW * f} y1={PAD}
            x2={PAD + plotW * f} y2={PAD + plotH}
            stroke="#1e293b" strokeDasharray="1 3"
          />
        ))}
        {bins ? (
          <g style={{ mixBlendMode: "screen" }}>
            <path d={path("r")} fill="#ef4444" fillOpacity="0.7" stroke="#ef4444" strokeWidth="0.5" />
            <path d={path("g")} fill="#22c55e" fillOpacity="0.7" stroke="#22c55e" strokeWidth="0.5" />
            <path d={path("b")} fill="#3b82f6" fillOpacity="0.7" stroke="#3b82f6" strokeWidth="0.5" />
          </g>
        ) : !err ? (
          <text
            x={W / 2} y={H / 2}
            textAnchor="middle" dominantBaseline="middle"
            className="fill-slate-600"
            style={{ fontSize: 10 }}
          >
            reading pixels…
          </text>
        ) : null}
      </svg>
    </div>
  );
}

